// Payments → Recurring: consolidated cross-PSP subscriptions report (executive view). Read-only over the LDB.
// Filters (PSP / account / status / mapped / search / date range) drive the tiles, the monthly collected-
// revenue chart (stacked bars by PSP + total line), the "what changed this period" strip, and the list.
// The list is CURRENT state; the date range scopes the chart + changes. Sub IDs hyperlink to the PSP recon.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Paper, Typography, FormControl, Select, MenuItem, TextField, Chip, Table, TableBody, TableCell,
  TableHead, TableRow, CircularProgress, Alert, Link, Tooltip, TablePagination, Button,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip as RTooltip, Legend, CartesianGrid, ResponsiveContainer,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../components/common/PageLayout';
import DonorLink from '../components/recon/DonorLink';
import { pledgeStatusMeta } from '../components/recon/reconShared';
import SubscriptionDetailDialog from '../components/pledges/SubscriptionDetailDialog';
import AutocreateLogDialog from '../components/recon/AutocreateLogDialog';
import { get } from '../utils/api';

const PROVIDER_LABEL = { paysimple: 'PaySimple', authnet: 'Authorize.Net' };
const PSP_COLOR = { paysimple: '#00b4a0', authnet: '#1f6feb' };
const fmtMoney = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtMoney2 = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—');
const fmtMonth = (ym) => { const [y, m] = String(ym).split('-'); return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }); };
const statusColor = (s) => (s === 'active' ? 'success' : s === 'suspended' ? 'warning' : s === 'expired' ? 'default' : 'default');

function computeRange(preset, cStart, cEnd) {
  const now = new Date();
  const y = now.getFullYear(); const m = now.getMonth();
  const iso = (d) => d.toISOString();
  const first = (yr, mo) => new Date(yr, mo, 1);
  const end = (yr, mo) => new Date(yr, mo + 1, 0, 23, 59, 59, 999);
  switch (preset) {
    case 'all': return { start: null, end: null };
    case 'this_month': return { start: iso(first(y, m)), end: iso(now) };
    case 'last_month': return { start: iso(first(y, m - 1)), end: iso(end(y, m - 1)) };
    case 'this_year': return { start: iso(first(y, 0)), end: iso(now) };
    case 'last_year': return { start: iso(first(y - 1, 0)), end: iso(end(y - 1, 11)) };
    case 'last_6m': return { start: iso(first(y, m - 5)), end: iso(now) };
    case 'custom': return { start: cStart ? `${cStart}T00:00:00.000Z` : null, end: cEnd ? `${cEnd}T23:59:59.999Z` : null };
    case '12m': default: return { start: iso(first(y, m - 11)), end: iso(now) };
  }
}

function Tile({ label, value, sub, color, onClick, active }) {
  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      sx={{
        p: 2, height: '100%', borderTop: 3, borderTopColor: color ? `${color}.main` : 'divider', borderRadius: 2,
        ...(onClick ? { cursor: 'pointer', transition: 'box-shadow .15s, border-color .15s', '&:hover': { boxShadow: 3 } } : {}),
        ...(active ? { boxShadow: 3, borderColor: color ? `${color}.main` : 'primary.main' } : {}),
      }}
    >
      <Typography variant="caption" sx={{ color: color ? `${color}.dark` : 'text.secondary', fontWeight: 600 }}>{label}</Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5, color: color ? `${color}.dark` : 'text.primary' }}>{value}</Typography>
      {sub && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{sub}</Typography>}
    </Paper>
  );
}

export default function RecurringReportPage() {
  const navigate = useNavigate();
  const [psp, setPsp] = useState('all');
  const [account, setAccount] = useState('all');
  const [logOpen, setLogOpen] = useState(false);
  const [status, setStatus] = useState('active'); // default view: active subscriptions
  const [mapped, setMapped] = useState('all');
  const [q, setQ] = useState('');
  const [range, setRange] = useState('12m');
  const [cStart, setCStart] = useState('');
  const [cEnd, setCEnd] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [detailSub, setDetailSub] = useState(null); // {provider, sub_id, account} for the detail dialog
  const [page, setPage] = useState(0); // 50 rows per page
  const ROWS = 50;

  const { start, end } = useMemo(() => computeRange(range, cStart, cEnd), [range, cStart, cEnd]);
  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try { setData(await get('/api/manage/recurring-report', { psp, account, status, mapped, q, start: start || '', end: end || '' })); }
    catch (e) { setErr(e?.response?.data?.error || e?.message || 'Failed to load report'); }
    finally { setLoading(false); }
  }, [psp, account, status, mapped, q, start, end]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(0); }, [psp, account, status, mapped, q, start, end]); // back to first page on any filter change

  const tiles = data?.tiles || {};
  const changes = data?.changes || {};
  const revenue = data?.revenue || [];
  const subs = data?.subscriptions || [];
  const pageSubs = subs.slice(page * ROWS, page * ROWS + ROWS);

  // Tile click → drill the list down to the records behind that number (status / mapping filters).
  const drill = (patch) => { setStatus(patch.status ?? 'all'); setMapped(patch.mapped ?? 'all'); setQ(''); };

  const sel = (label, value, set, opts) => (
    <FormControl size="small"><Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.25 }}>{label}</Typography>
      <Select value={value} onChange={(e) => set(e.target.value)} sx={{ minWidth: 150 }}>
        {opts.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
      </Select>
    </FormControl>
  );

  return (
    <PageLayout contentMaxWidth="xl" showHeaderImage={false} showOrgHeader>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Recurring Subscriptions</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>Consolidated subscriptions across PaySimple + Authorize.Net</Typography>
          <Button size="small" variant="outlined" startIcon={<HistoryIcon />} onClick={() => setLogOpen(true)}>Auto-create log</Button>
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-end', mb: 2 }}>
        {sel('Providers', psp, setPsp, [{ value: 'all', label: 'All Providers' }, { value: 'paysimple', label: 'PaySimple' }, { value: 'authnet', label: 'Authorize.Net' }])}
        {sel('Account', account, setAccount, [{ value: 'all', label: 'All accounts' }, { value: 'icct', label: 'ICCT' }, { value: 'kotilinga', label: 'Kotilinga' }])}
        {sel('Status', status, setStatus, [{ value: 'all', label: 'All statuses' }, { value: 'active', label: 'Active' }, { value: 'suspended', label: 'Suspended' }, { value: 'expired', label: 'Expired' }, { value: 'suspended,expired', label: 'Suspended or expired' }, { value: 'canceled', label: 'Canceled' }])}
        {sel('Mapping', mapped, setMapped, [{ value: 'all', label: 'All' }, { value: 'mapped', label: 'Mapped' }, { value: 'unmapped', label: 'Unmapped' }])}
        <FormControl size="small"><Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.25 }}>Search</Typography>
          <TextField size="small" placeholder="Donor / sub id…" value={q} onChange={(e) => setQ(e.target.value)} sx={{ minWidth: 180 }} /></FormControl>
        <FormControl size="small"><Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.25 }}>Range (chart)</Typography>
          <Select value={range} onChange={(e) => setRange(e.target.value)} sx={{ minWidth: 150 }}>
            <MenuItem value="this_month">This month</MenuItem>
            <MenuItem value="last_month">Last month</MenuItem>
            <MenuItem value="this_year">This year</MenuItem>
            <MenuItem value="last_year">Last year</MenuItem>
            <MenuItem value="last_6m">Last 6 months</MenuItem>
            <MenuItem value="12m">Last 12 months</MenuItem>
            <MenuItem value="all">All time</MenuItem>
            <MenuItem value="custom">Custom…</MenuItem>
          </Select></FormControl>
        {range === 'custom' && (<>
          <TextField size="small" type="date" label="Start" InputLabelProps={{ shrink: true }} value={cStart} onChange={(e) => setCStart(e.target.value)} />
          <TextField size="small" type="date" label="End" InputLabelProps={{ shrink: true }} value={cEnd} onChange={(e) => setCEnd(e.target.value)} />
        </>)}
      </Box>

      {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{err}</Alert>}

      {/* Tiles */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(3,1fr)', lg: 'repeat(6,1fr)' }, gap: 1.5, mb: 2 }}>
        <Tile label="Subscriptions" value={tiles.total ?? '—'} sub={`PaySimple ${tiles.by_psp?.paysimple ?? 0} · Authorize.Net ${tiles.by_psp?.authnet ?? 0}`} color="primary"
          onClick={() => drill({})} active={status === 'all' && mapped === 'all'} />
        <Tile label="Active" value={tiles.active ?? '—'} sub={`PaySimple ${tiles.active_by_psp?.paysimple ?? 0} · Authorize.Net ${tiles.active_by_psp?.authnet ?? 0}`} color="success"
          onClick={() => drill({ status: 'active' })} active={status === 'active' && mapped === 'all'} />
        <Tile label="Monthly recurring" value={fmtMoney(tiles.mrr)} sub="active subscriptions" color="success"
          onClick={() => drill({ status: 'active' })} active={status === 'active' && mapped === 'all'} />
        <Tile label="Mapped" value={tiles.mapped ?? '—'} color="success"
          onClick={() => drill({ mapped: 'mapped' })} active={mapped === 'mapped'} />
        <Tile label="Unmapped" value={tiles.unmapped ?? '—'} sub="need setup" color="warning"
          onClick={() => drill({ mapped: 'unmapped' })} active={mapped === 'unmapped'} />
        <Tile label="Suspended / Expired" value={`${tiles.suspended ?? 0} / ${tiles.expired ?? 0}`} color="default"
          onClick={() => drill({ status: 'suspended,expired' })} active={status === 'suspended,expired'} />
      </Box>

      {/* What changed this period */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Typography variant="overline" sx={{ color: 'text.secondary' }}>Changes in range</Typography>
        <Chip size="small" color="success" variant="outlined" label={`New ${changes.added || 0}`} />
        <Chip size="small" color="info" variant="outlined" label={`Resumed ${changes.reactivated || 0}`} />
        <Chip size="small" color="warning" variant="outlined" label={`Suspended ${changes.suspended || 0}`} />
        <Chip size="small" color="error" variant="outlined" label={`Canceled ${changes.canceled || 0}`} />
        <Chip size="small" variant="outlined" label={`Ended ${changes.ended || 0}`} />
      </Paper>

      {/* Revenue chart */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Collected recurring revenue by month</Typography>
        {loading ? <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={22} /></Box>
          : revenue.length === 0 ? <Typography variant="body2" sx={{ color: 'text.secondary', py: 4, textAlign: 'center' }}>No revenue in range.</Typography>
            : (
              <ResponsiveContainer width="100%" height={320}>
                <ComposedChart data={revenue} margin={{ top: 8, right: 16, bottom: 0, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="month" tickFormatter={fmtMonth} fontSize={12} />
                  <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} fontSize={12} />
                  <RTooltip formatter={(v, n) => [fmtMoney2(v), n === 'total' ? 'Total' : PROVIDER_LABEL[n] || n]} labelFormatter={fmtMonth} />
                  <Legend formatter={(v) => (v === 'total' ? 'Total' : PROVIDER_LABEL[v] || v)} />
                  <Bar dataKey="paysimple" stackId="rev" fill={PSP_COLOR.paysimple} />
                  <Bar dataKey="authnet" stackId="rev" fill={PSP_COLOR.authnet} />
                  <Line type="monotone" dataKey="total" stroke="#6a1b9a" strokeWidth={2} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            )}
      </Paper>

      {/* Subscriptions list */}
      <Paper variant="outlined">
        <Box sx={{ px: 2, py: 1, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {subs.length} subscription{subs.length === 1 ? '' : 's'} (current state)
            {subs.length > ROWS ? ` — showing ${page * ROWS + 1}–${Math.min((page + 1) * ROWS, subs.length)}` : ''}
          </Typography>
        </Box>
        {loading ? <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={22} /></Box>
          : subs.length === 0 ? <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>No subscriptions match.</Box>
            : (
              <Table size="small" sx={{ '& td, & th': { px: 1.25 } }}>
                <TableHead><TableRow>
                  <TableCell>Provider</TableCell><TableCell>Account</TableCell><TableCell>Sub ID</TableCell><TableCell>Donor</TableCell>
                  <TableCell align="right">Amount</TableCell><TableCell>Status</TableCell><TableCell>Next payment</TableCell>
                  <TableCell>Allocation</TableCell><TableCell>Pledge</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {pageSubs.map((s) => (
                    <TableRow key={`${s.provider}:${s.account}:${s.sub_id}`} hover>
                      <TableCell><Chip size="small" variant="outlined" label={PROVIDER_LABEL[s.provider] || s.provider} /></TableCell>
                      <TableCell sx={{ textTransform: 'capitalize' }}>{s.account}</TableCell>
                      <TableCell>
                        <Tooltip title="View subscription details + payments">
                          <Link component="button" type="button" sx={{ fontFamily: 'monospace' }} onClick={() => setDetailSub(s)}>{s.sub_id}</Link>
                        </Tooltip>
                      </TableCell>
                      <TableCell><DonorLink name={s.customer_name || s.donor_name} customerId={s.customer_id} /></TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{fmtMoney2(s.amount)}<Typography variant="caption" sx={{ color: 'text.secondary' }}>/mo</Typography></TableCell>
                      <TableCell><Chip size="small" variant="outlined" color={statusColor(s.status)} label={s.status_raw || s.status} /></TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>{fmtDate(s.next_payment_date)}</TableCell>
                      <TableCell sx={{ maxWidth: 420 }}>
                        {!s.mapped ? <Chip size="small" color="warning" variant="filled" label="Unmapped" />
                          : (s.allocations && s.allocations.length ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                              {s.allocations.map((a, i) => (
                                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
                                  <Typography variant="body2">{a.label}</Typography>
                                  <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>{fmtMoney2(a.amount)}</Typography>
                                </Box>
                              ))}
                            </Box>
                          ) : <Typography variant="body2">Mapped</Typography>)}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {s.pledge_order ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25 }}>
                            <Tooltip title="Open the pledge order">
                              <Link component="button" type="button" sx={{ fontWeight: 600 }} onClick={() => navigate(`/orders/${s.pledge_order.id}/edit`)}>
                                #{s.pledge_order.order_number || s.pledge_order.id}
                              </Link>
                            </Tooltip>
                            {pledgeStatusMeta(s.pledge_order.status) && (
                              <Chip size="small" variant="outlined" color={pledgeStatusMeta(s.pledge_order.status).color} label={pledgeStatusMeta(s.pledge_order.status).label} />
                            )}
                          </Box>
                        ) : <Typography variant="caption" sx={{ color: 'text.secondary' }}>—</Typography>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
        {subs.length > ROWS && (
          <TablePagination
            component="div"
            count={subs.length}
            page={page}
            onPageChange={(e, p) => setPage(p)}
            rowsPerPage={ROWS}
            rowsPerPageOptions={[ROWS]}
            labelRowsPerPage="Per page"
          />
        )}
      </Paper>
      <SubscriptionDetailDialog open={!!detailSub} onClose={() => setDetailSub(null)} provider={detailSub?.provider} subId={detailSub?.sub_id} account={detailSub?.account} />
      <AutocreateLogDialog open={logOpen} onClose={() => setLogOpen(false)} />
    </PageLayout>
  );
}
